import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FaChartLine, FaCalendarAlt, FaBullseye, FaMoneyBillWave, FaFileAlt, FaUsers, FaArrowRight, FaMoon, FaSun, FaChartPie, FaCheck, FaRegLightbulb } from 'react-icons/fa';
import { motion } from 'framer-motion';

const LandingPage = () => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('cashsule-theme');
    return savedTheme === 'dark';
  });
  const ctaSectionRef = useRef(null); // Reference to the CTA section
  const [previewMode, setPreviewMode] = useState('desktop');
  const [installTab, setInstallTab] = useState('ios'); // Add this new state for installation tabs

  // Toggle dark mode
  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    localStorage.setItem('cashsule-theme', newDarkMode ? 'dark' : 'light');
  };

  // Function to scroll to the CTA section
  const scrollToCta = () => {
    ctaSectionRef.current?.scrollIntoView({ 
      behavior: 'smooth',
      block: 'start'
    });
  };

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    // Create a class that only applies to elements within this component
    const landingPageContainer = document.getElementById('landing-page-container');
    
    if (isDarkMode) {
      landingPageContainer.classList.add('dark');
    } else {
      landingPageContainer.classList.remove('dark');
    }
    
    // Cleanup - ensure we don't leave the 'dark' class on the body
    return () => {
      document.body.classList.remove('dark');
    };
  }, [isDarkMode]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };

  // Features data
  const features = [
    {
      icon: <FaChartLine className="text-4xl text-primary-600 mb-4" />,
      title: "Monthly & Lifetime Expenses Tracking",
      description: "Keep track of all your expenses in one place. Categorise and visualise your spending patterns.",
      gradient: "from-rose-400 to-pink-600"
    },
    {
      icon: <FaCalendarAlt className="text-4xl text-primary-600 mb-4" />,
      title: "Recurring Payments",
      description: "Manage subscriptions, fixed term payments and one-off expenses with automatic transformation into your expense records.",
      gradient: "from-cyan-400 to-blue-600"
    },
    {
      icon: <FaBullseye className="text-4xl text-primary-600 mb-4" />,
      title: "Budget Plans",
      description: "Set realistic budgets for different categories and track your progress in real-time.",
      gradient: "from-emerald-400 to-green-600"
    },
    {
      icon: <FaMoneyBillWave className="text-4xl text-primary-600 mb-4" />,
      title: "Income Tracking",
      description: "Log all your income sources and get a complete picture of your financial health.",
      gradient: "from-purple-400 to-fuchsia-600"
    },
    {
      icon: <FaFileAlt className="text-4xl text-primary-600 mb-4" />,
      title: "Statement Generation",
      description: "Export your financial data into professional PDF statements for easy record-keeping.",
      gradient: "from-red-400 to-rose-600"
    },
    {
      icon: <FaUsers className="text-4xl text-primary-600 mb-4" />,
      title: "Community Board",
      description: "Connect with others, share tips and learn financial strategies from the community. (Coming Soon)",
      gradient: "from-yellow-400 to-amber-600"
    }
  ];

  return (
    <div id="landing-page-container" className="landing-page w-full overflow-x-hidden select-none">
      {/* Navigation with transparency and backdrop blur */}
      <nav className="backdrop-blur-lg bg-white/70 dark:bg-gray-900/70 fixed w-full z-50 shadow-sm border-b border-gray-200/50 dark:border-gray-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <img
                src="/cashsule.png"
                alt="Cashsule Icon"
                className="h-8 w-auto sm:h-10"
              />
              <h1 className="ml-2 sm:ml-3 text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                Cashsule
              </h1>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4">
              <button
                onClick={toggleDarkMode}
                className="p-2 rounded-full bg-gray-100/80 dark:bg-gray-800/80 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                aria-label="Toggle dark mode"
              >
                {isDarkMode ? <FaSun className="h-5 w-5" /> : <FaMoon className="h-5 w-5" />}
              </button>
              <Link
                to="/login"
                className="px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg text-primary-600 dark:text-primary-400 hover:bg-gray-100/80 dark:hover:bg-gray-800/80 transition-colors text-sm sm:text-base"
              >
                Log in
              </Link>
              <Link
                to="/register"
                className="px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors text-sm sm:text-base"
              >
                Sign up
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 sm:pt-26 pb-2 sm:pb-4 px-4 background-animate bg-gradient-to-br from-purple-100 via-blue-100 to-green-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 w-full">
        <div className="w-full max-w-7xl mx-auto">
          <motion.div
            className="text-center w-full"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-gray-900 dark:text-white mb-4 sm:mb-6">
              <span className="block sm:inline">Your Personal</span>{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-purple-600">Cash Capsule</span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto mb-8 sm:mb-10">
              Track expenses, manage subscriptions, and achieve your financial goals all in one place.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 w-full max-w-md mx-auto">
              <button
                onClick={scrollToCta}
                className="w-full px-6 py-3 rounded-xl bg-gradient-to-r from-primary-600 to-purple-600 text-white text-base font-semibold hover:shadow-lg transition-shadow flex items-center justify-center gap-2 background-animate"
              >
                Get Started <FaArrowRight />
              </button>
              <Link
                to="/login"
                className="w-full px-6 py-3 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-700 text-base font-semibold hover:shadow-lg transition-shadow"
              >
                Log in to your account
              </Link>
            </div>
          </motion.div>

          {/* Decorative elements only - no preview here anymore */}
          <motion.div
            className="mt-12 relative w-full"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            {/* Adjust decorative elements for mobile */}
            <div className="absolute -z-10 top-1/2 left-0 transform -translate-x-1/2 -translate-y-1/2 hidden sm:block">
              <div className="w-32 sm:w-64 h-32 sm:h-64 rounded-full bg-purple-300/30 dark:bg-purple-900/20 blur-3xl"></div>
            </div>
            <div className="absolute -z-10 top-1/3 right-0 transform translate-x-1/3 hidden sm:block">
              <div className="w-32 sm:w-72 h-32 sm:h-72 rounded-full bg-primary-300/30 dark:bg-primary-900/20 blur-3xl"></div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 sm:py-20 px-4 bg-gray-50 dark:bg-gradient-to-br dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 w-full">
        <div className="w-full max-w-7xl mx-auto">
          <motion.div
            className="text-center mb-12 sm:mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4 px-2">
              Powerful Features for Your Financial Journey
            </h2>
            <p className="text-lg sm:text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto px-2">
              Cashsule provides all the tools you need to manage your finances efficiently.
            </p>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="bg-white dark:bg-white/10 backdrop-blur-lg rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300 border border-gray-200 dark:border-gray-700"
                variants={itemVariants}
                whileHover={{ y: -5 }}
                whileTap={{ y: 0 }}
              >
                <div className={`h-2 bg-gradient-to-r ${feature.gradient}`}></div>
                <div className="p-6 sm:p-8">
                  {feature.icon}
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3">{feature.title}</h3>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* IntelliSpend Section */}
      <section className="py-16 sm:py-20 px-4 bg-white dark:bg-gray-800 w-full">
        <div className="w-full max-w-7xl mx-auto">
          <motion.div
            className="text-center mb-12 sm:mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              IntelliSpend
            </h2>
            <p className="text-lg sm:text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
              Get deep insights into your spending patterns and financial health
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Category Analysis Feature */}
            <motion.div
              className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6 shadow-lg"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center mb-4">
                <div className="p-3 rounded-lg bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 mr-4">
                  <FaChartPie className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Category Analysis</h3>
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Visualise your spending patterns with detailed category breakdowns and trends.
              </p>
              <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                <li className="flex items-center">
                  <FaCheck className="text-green-500 mr-2" />
                  Track spending by category over time
                </li>
                <li className="flex items-center">
                  <FaCheck className="text-green-500 mr-2" />
                  Compare monthly spending patterns
                </li>
                <li className="flex items-center">
                  <FaCheck className="text-green-500 mr-2" />
                  Identify your highest spending categories
                </li>
                <li className="flex items-center">
                  <FaCheck className="text-green-500 mr-2" />
                  Set custom category filters for detailed analysis
                </li>
              </ul>
            </motion.div>

            {/* Smart Insights Feature */}
            <motion.div
              className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6 shadow-lg"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center mb-4">
                <div className="p-3 rounded-lg bg-purple-100 dark:bg-purple-900/50 text-purple-600 dark:text-purple-400 mr-4">
                  <FaRegLightbulb className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Smart Insights</h3>
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Get actionable insights and recommendations based on your spending patterns.
              </p>
              <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                <li className="flex items-center">
                  <FaCheck className="text-green-500 mr-2" />
                  Monthly spending trends and comparisons
                </li>
                <li className="flex items-center">
                  <FaCheck className="text-green-500 mr-2" />
                  Potential savings opportunities
                </li>
                <li className="flex items-center">
                  <FaCheck className="text-green-500 mr-2" />
                  Customisable date range analysis
                </li>
                <li className="flex items-center">
                  <FaCheck className="text-green-500 mr-2" />
                  Detailed financial reports and recommendations
                </li>
              </ul>
            </motion.div>
          </div>

          {/* <motion.div
            className="text-center mt-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <Link
              to="/financial-preview"
              className="inline-flex items-center px-6 py-3 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold hover:shadow-lg transition-shadow"
            >
              Explore Financial Preview <FaArrowRight className="ml-2" />
            </Link>
          </motion.div> */}
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 sm:py-20 px-4 bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 w-full">
        <div className="w-full max-w-7xl mx-auto">
          <motion.div
            className="text-center mb-12 sm:mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              How Cashsule Works
            </h2>
            <p className="text-lg sm:text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
              Achieving financial clarity in three simple steps
            </p>
          </motion.div>

          <div className="flex flex-col md:flex-row gap-6 md:gap-4 items-stretch">
            <motion.div
              className="flex-1"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.2 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
            >
              <div className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white rounded-2xl p-6 sm:p-8 h-full shadow-lg">
                <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">1. Track Your Finances</h3>
                <p className="mb-6 text-sm sm:text-base">
                  Log your income and expenses easily with our intuitive interface. Categorise transactions and get insights into your spending habits.
                </p>
                <div className="w-16 sm:w-20 h-1 bg-white/30 rounded-full"></div>
              </div>
            </motion.div>

            <motion.div 
              className="flex-1"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
            >
              <div className="bg-gradient-to-br from-blue-500 to-cyan-400 text-white rounded-2xl p-6 sm:p-8 h-full shadow-lg">
                <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">2. Set Financial Goals</h3>
                <p className="mb-6 text-sm sm:text-base">
                  Create personalised budget plans and savings goals. Monitor your progress with visual charts and receive timely notifications.
                </p>
                <div className="w-16 sm:w-20 h-1 bg-white/30 rounded-full"></div>
              </div>
            </motion.div>

            <motion.div 
              className="flex-1"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.2}}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
            >
              <div className="bg-gradient-to-br from-emerald-500 to-teal-400 text-white rounded-2xl p-6 sm:p-8 h-full shadow-lg">
                <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">3. Achieve Financial Freedom</h3>
                <p className="mb-6 text-sm sm:text-base">
                  Get detailed reports and statements. Make informed decisions based on your financial patterns and reach your financial goals.
                </p>
                <div className="w-16 sm:w-20 h-1 bg-white/30 rounded-full"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PWA Installation Section */}
      <section className="py-16 sm:py-20 px-4 bg-white dark:bg-gray-800 w-full">
        <div className="w-full max-w-7xl mx-auto">
          <motion.div
            className="text-center mb-12 sm:mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Access Cashsule Anywhere
            </h2>
            <p className="text-lg sm:text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
              Install Cashsule on your device for a seamless, app-like experience
            </p>
          </motion.div>

          <div className="flex flex-col lg:flex-row gap-10 items-center">
            {/* Text Content */}
            <motion.div
              className="lg:w-1/2"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-xl p-6 sm:p-8 shadow-lg border border-gray-200 dark:border-gray-700">
                <h3 className="text-xl sm:text-2xl font-bold mb-4 text-gray-900 dark:text-white">Use Cashsule Like a Native App</h3>
                
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-primary-600 dark:text-primary-400 mt-0.5 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-gray-700 dark:text-gray-300">Add Cashsule to your home screen for instant access</p>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-primary-600 dark:text-primary-400 mt-0.5 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-gray-700 dark:text-gray-300">Looks and feels like a native app, with full-screen experience</p>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-primary-600 dark:text-primary-400 mt-0.5 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-gray-700 dark:text-gray-300">No app store or download required - simply tap "Add to Home Screen"</p>
                  </li>
                </ul>

                <div className="mt-6 p-4 bg-gray-100 dark:bg-gray-700/60 rounded-lg">
                  <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">How to install:</p>
                  
                  {/* iOS/Android tab navigation */}
                  <div className="flex border-b border-gray-300 dark:border-gray-600 mb-3">
                    <button 
                      onClick={() => setInstallTab('ios')}
                      className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors duration-200 ${
                        installTab === 'ios' 
                          ? 'border-primary-600 text-primary-600 dark:text-primary-400' 
                          : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                      }`}
                    >
                      iOS (Safari)
                    </button>
                    <button 
                      onClick={() => setInstallTab('android')}
                      className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors duration-200 ${
                        installTab === 'android' 
                          ? 'border-primary-600 text-primary-600 dark:text-primary-400' 
                          : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                      }`}
                    >
                      Android
                    </button>
                  </div>
                  
                  {/* iOS instructions */}
                  <div 
                    className="transition-opacity duration-300"
                    style={{ 
                      display: installTab === 'ios' ? 'block' : 'none',
                    }}
                  >
                    <ol className="text-sm text-gray-700 dark:text-gray-300 space-y-1 list-decimal ml-4">
                      <li>Open Cashsule in <strong>Safari</strong> browser</li>
                      <li>Tap the <strong>Share</strong> icon (rectangle with arrow) at the bottom of the screen</li>
                      <li>Scroll down and tap <strong>Add to Home Screen</strong></li>
                      <li>Tap <strong>Add</strong> in the top-right corner</li>
                    </ol>
                  </div>
                  
                  {/* Android instructions */}
                  <div 
                    className="transition-opacity duration-300"
                    style={{ 
                      display: installTab === 'android' ? 'block' : 'none',
                    }}
                  >
                    <ol className="text-sm text-gray-700 dark:text-gray-300 space-y-1 list-decimal ml-4">
                      <li>Open Cashsule in <strong>Chrome</strong> browser</li>
                      <li>Tap the <strong>three dots</strong> menu in the top-right corner</li>
                      <li>Tap <strong>Install app</strong> or <strong>Add to Home screen</strong></li>
                      <li>Follow the prompts to complete installation</li>
                    </ol>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Preview section */}
            <motion.div
              className="w-full lg:w-1/2"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                <div className="h-6 bg-gray-100 dark:bg-gray-700 flex items-center px-2">
                  <div className="flex space-x-1.5">
                    <div className="w-2 h-2 rounded-full bg-red-500"></div>
                    <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  </div>
                </div>
                
                {/* Tab Navigation - More browser-like */}
                <div className="flex border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                  <button 
                    onClick={() => setPreviewMode('desktop')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors duration-200 ${
                      previewMode === 'desktop' 
                        ? 'border-primary-600 text-primary-600 dark:text-primary-400' 
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                    }`}
                  >
                    Desktop
                  </button>
                  <button 
                    onClick={() => setPreviewMode('mobile')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors duration-200 ${
                      previewMode === 'mobile' 
                        ? 'border-primary-600 text-primary-600 dark:text-primary-400' 
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                    }`}
                  >
                    Mobile
                  </button>
                </div>
                
                <div className="flex justify-center p-4 bg-gray-50 dark:bg-gray-800">
                  <div className="relative w-full" style={{ height: "300px" }}>
                    {/* Container for desktop image */}
                    <div 
                      className="absolute inset-0 flex items-center justify-center transition-opacity duration-300"
                      style={{ 
                        opacity: previewMode === 'desktop' ? 1 : 0,
                        pointerEvents: previewMode === 'desktop' ? 'auto' : 'none'
                      }}
                    >
                      <img
                        src="/cashsuledashboarddemo_desktop.png"
                        alt="Cashsule Dashboard desktop preview"
                        className="max-h-[280px] w-auto object-contain shadow-md rounded border border-gray-200 dark:border-gray-700"
                      />
                    </div>
                    
                    {/* Container for mobile image */}
                    <div 
                      className="absolute inset-0 flex items-center justify-center transition-opacity duration-300"
                      style={{ 
                        opacity: previewMode === 'mobile' ? 1 : 0,
                        pointerEvents: previewMode === 'mobile' ? 'auto' : 'none'
                      }}
                    >
                      <img
                        src="/cashsuledashboarddemo_mobile.png"
                        alt="Cashsule Dashboard mobile preview"
                        className="max-h-[280px] w-auto object-contain shadow-md rounded border border-gray-200 dark:border-gray-700"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Call to Action Section - Add ref to scroll to */}
      <section ref={ctaSectionRef} className="py-16 sm:py-20 px-4 background-animate bg-gradient-to-br from-gray-100 via-primary-50 to-purple-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 w-full">
        <div className="w-full max-w-7xl flex justify-center items-center mx-auto">
          <motion.div
            className="text-center m-4 sm:m-8"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4 px-2">
                Ready to Take Control of Your Finances?
            </h2>
            <p className="text-lg sm:text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto mb-8 sm:mb-10 px-2">
                Transform how you manage your finances with our simple yet powerful tools.
            </p>
            <Link
              to="/register"
              className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 rounded-xl bg-gradient-to-r from-primary-600 to-purple-600 text-white text-base sm:text-lg font-semibold hover:shadow-lg transition-shadow inline-flex items-center justify-center gap-2 background-animate hover:scale-105 transform transition-transform duration-300"
            >
              Start Your Free Account <FaArrowRight />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 py-10 sm:py-12 px-4 w-full">
        <div className="w-full max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center mb-8 md:mb-0">
              <img
                src="/cashsule.png"
                alt="Cashsule Icon"
                className="h-8 sm:h-10 w-auto"
              />
              <h1 className="ml-2 sm:ml-3 text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                Cashsule
              </h1>
            </div>
            <div className="flex flex-wrap justify-center gap-5 md:gap-8">
              <Link to="/register" className="text-sm sm:text-base text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">Sign Up</Link>
              <Link to="/login" className="text-sm sm:text-base text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">Login</Link>
              <Link to="/privacy" className="text-sm sm:text-base text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="text-sm sm:text-base text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">Terms of Service</Link>
            </div>
          </div>
          <div className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t border-gray-200 dark:border-gray-800 text-center text-gray-500 dark:text-gray-400 text-sm">
            © {new Date().getFullYear()} Cashsule. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;

